import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EmailInviteLanguage,
  MarketDialogueStatus,
  OrganizationType,
  type MarketDialogueDto,
  type MinimalOrgDto,
  type MarketDialogueInvitationDto,
  useInviteEmailToMarketDialogueMutation,
  useInviteOrganizationToMarketDialogueMutation,
  useListMarketDialogueSupplierSuggestionsQuery,
  useWithdrawMarketDialogueInvitationMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { OrganizationSelector } from "../../common/organization/OrganizationSelector";
import { MarketDialogueFormSection } from "./MarketDialogueFormSection";

export const MarketDialogueInvitations = ({ dialogue }: { dialogue: MarketDialogueDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [inviteOrganization, { isLoading: isInvitingOrganization }] = useInviteOrganizationToMarketDialogueMutation();
  const [inviteEmail, { isLoading: isInvitingEmail }] = useInviteEmailToMarketDialogueMutation();
  const ownOrganizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const [chosen, setChosen] = useState<MinimalOrgDto | undefined>();
  const [email, setEmail] = useState("");
  const [emailChecked, setEmailChecked] = useState(false);
  const [language, setLanguage] = useState<EmailInviteLanguage>(EmailInviteLanguage.No);
  const invitations = dialogue.invitations ?? [];
  const invitedOrganizationIds = invitations.flatMap((invitation) =>
    invitation.organizationId ? [invitation.organizationId] : [],
  );
  const emailProblem = invitationEmailProblem(email, invitations, t);

  const invite = async (organizationId: string) => {
    try {
      await inviteOrganization({
        marketDialogueId: dialogue.id,
        inviteOrganizationToMarketDialogueRequest: { organizationId },
      }).unwrap();
    } catch (e) {
      trigger(e);
    } finally {
      setChosen(undefined);
    }
  };

  const inviteByEmail = async () => {
    setEmailChecked(true);
    if (emailProblem) return;
    try {
      await inviteEmail({
        marketDialogueId: dialogue.id,
        inviteEmailToMarketDialogueRequest: { email: email.trim(), language },
      }).unwrap();
      setEmail("");
      setEmailChecked(false);
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <MarketDialogueFormSection heading={t("Invited suppliers")}>
      <Stack spacing="5">
        <Text fontSize="sm" color="smMuted">
          {whenInvitationsGoOut(dialogue, t)}
        </Text>
        {invitations.length === 0 ? (
          <Text fontSize="sm" color="smMuted">
            {t("No suppliers are invited yet.")}
          </Text>
        ) : (
          <Stack spacing="2" data-testid="market-dialogue-invitations">
            {invitations.map((invitation) => (
              <InvitationRow key={invitation.id} dialogueId={dialogue.id} invitation={invitation} />
            ))}
          </Stack>
        )}
        {dialogue.canInvite && (
          <>
            <FormControl>
              <FormLabel>{t("Invite a registered supplier")}</FormLabel>
              <OrganizationSelector
                size="md"
                type={OrganizationType.Organization}
                organization={chosen}
                orgsToExclude={[...invitedOrganizationIds, ownOrganizationId]}
                placeholder={t("Search by name or organisation number") ?? undefined}
                isClearable={false}
                isDisabled={isInvitingOrganization}
                onChange={({ organization }) => {
                  if (!organization) return;
                  setChosen(organization);
                  void invite(organization.id);
                }}
              />
            </FormControl>
            <FormControl isInvalid={emailChecked && emailProblem !== null}>
              <FormLabel>{t("Invite a supplier not registered in SourceMagnet, by email")}</FormLabel>
              <Flex gap="2" wrap="wrap">
                <Input
                  type="email"
                  flex="1"
                  minW="220px"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailChecked(email.trim() !== "")}
                  placeholder={t("Email address") ?? undefined}
                />
                <Select
                  w="auto"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as EmailInviteLanguage)}
                  aria-label={t("Language of the email") ?? undefined}
                >
                  <option value={EmailInviteLanguage.No}>{t("Norwegian")}</option>
                  <option value={EmailInviteLanguage.En}>{t("English")}</option>
                </Select>
                <Button
                  colorScheme="teal"
                  onClick={() => void inviteByEmail()}
                  isLoading={isInvitingEmail}
                  isDisabled={email.trim() === "" || (emailChecked && emailProblem !== null)}
                >
                  {t("Invite")}
                </Button>
              </Flex>
              {emailProblem && <FormErrorMessage>{emailProblem}</FormErrorMessage>}
            </FormControl>
            {dialogue.canAnnounceOnDoffin && (
              <SuggestedSuppliers dialogue={dialogue} onInvite={invite} isInviting={isInvitingOrganization} />
            )}
          </>
        )}
      </Stack>
    </MarketDialogueFormSection>
  );
};

const EMAIL_ADDRESS = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const invitationEmailProblem = (
  email: string,
  invitations: MarketDialogueInvitationDto[],
  t: (key: string) => string,
): string | null => {
  const address = email.trim();
  if (address === "") return null;
  if (!EMAIL_ADDRESS.test(address)) return t("That is not a valid email address.");
  const alreadyInvited = invitations.some((invitation) => invitation.email?.toLowerCase() === address.toLowerCase());
  return alreadyInvited ? t("This supplier is already invited.") : null;
};

const whenInvitationsGoOut = (dialogue: MarketDialogueDto, t: (key: string) => string): string => {
  if (dialogue.status === MarketDialogueStatus.Published) return t("New invitations are sent at once.");
  if (dialogue.announceOnDoffin) return t("The invitations are sent by email once Doffin has published the notice.");
  return t("The invitations are sent by email when you open the market dialogue.");
};

const InvitationRow = ({ dialogueId, invitation }: { dialogueId: string; invitation: MarketDialogueInvitationDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const displayDateWithTime = useDisplayDateWithTime();
  const [withdraw, { isLoading }] = useWithdrawMarketDialogueInvitationMutation();

  const onWithdraw = async () => {
    try {
      await withdraw({ marketDialogueId: dialogueId, invitationId: invitation.id }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Flex
      align="center"
      justify="space-between"
      gap="3"
      wrap="wrap"
      borderWidth="1px"
      borderColor="smBorder"
      rounded="md"
      px="3"
      py="2"
    >
      <Box minW="0">
        <Text fontSize="sm" fontWeight="medium" wordBreak="break-word">
          {invitation.organizationName ?? invitation.email}
        </Text>
        {invitation.email && (
          <Text fontSize="xs" color="smMuted">
            {t("Not registered in SourceMagnet")}
          </Text>
        )}
      </Box>
      <HStack spacing="2">
        {invitation.sentAt ? (
          <>
            <Tag size="sm" colorScheme="gray">
              {t("Invitation sent")}
            </Tag>
            <Text fontSize="xs" color="smMuted">
              {displayDateWithTime(invitation.sentAt)}
            </Text>
          </>
        ) : (
          <Tag size="sm" colorScheme="orange">
            {t("Not invited yet")}
          </Tag>
        )}
        {invitation.canWithdraw && (
          <Button size="sm" variant="ghost" colorScheme="red" onClick={() => void onWithdraw()} isLoading={isLoading}>
            {t("Remove")}
          </Button>
        )}
      </HStack>
    </Flex>
  );
};

const SuggestedSuppliers = ({
  dialogue,
  onInvite,
  isInviting,
}: {
  dialogue: MarketDialogueDto;
  onInvite: (organizationId: string) => Promise<void>;
  isInviting: boolean;
}) => {
  const { t } = useTranslation();
  const hasCpvCodes = dialogue.cpvCodes.length > 0;
  const { data, isLoading, isError, refetch } = useListMarketDialogueSupplierSuggestionsQuery(
    { marketDialogueId: dialogue.id },
    { skip: !hasCpvCodes },
  );
  const suppliers = data?.suppliers ?? [];

  return (
    <Box>
      <Text fontWeight="medium" mb="1">
        {t("Suggested suppliers")}
      </Text>
      <Text fontSize="sm" color="smMuted" mb="3">
        {t("Suppliers that have bid on your procurements with the same CPV codes as this market dialogue.")}
      </Text>
      {!hasCpvCodes ? (
        <Text fontSize="sm" color="smMuted">
          {t("Add CPV codes under «Basis» to get suggestions.")}
        </Text>
      ) : isLoading ? (
        <Text fontSize="sm" color="smMuted">
          {t("Loading")}...
        </Text>
      ) : isError ? (
        <HStack>
          <Text fontSize="sm" color="smError">
            {t("Could not load the suggestions.")}
          </Text>
          <Button size="sm" variant="link" colorScheme="teal" onClick={() => void refetch()}>
            {t("Try again")}
          </Button>
        </HStack>
      ) : suppliers.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No supplier has bid on your procurements with these CPV codes yet.")}
        </Text>
      ) : (
        <Stack spacing="2" data-testid="market-dialogue-suggestions">
          {suppliers.map((supplier) => (
            <Flex key={supplier.organizationId} align="center" justify="space-between" gap="3" wrap="wrap">
              <Box minW="0">
                <Text fontSize="sm">{supplier.name}</Text>
                <Text fontSize="xs" color="smMuted">
                  {t("Bid on {{number}} of your procurements with these CPV codes", {
                    number: supplier.matchingProcurements,
                  })}
                </Text>
              </Box>
              <Button size="sm" variant="outline" onClick={() => void onInvite(supplier.organizationId)} isDisabled={isInviting}>
                {t("Invite {{name}}", { name: supplier.name })}
              </Button>
            </Flex>
          ))}
        </Stack>
      )}
    </Box>
  );
};
